import { Switch } from "@/components/primitives/switch";
import { Loader } from "@/components/primitives/loader";
import { useState } from "react";
import { useWebPush } from "./useWebPush";
import { useTranslation } from "react-i18next";

/**
 * Purpose: Reusable switch component for toggling web push notifications
 *
 * How it works:
 * 1. Displays a switch toggle with optional loading state
 * 2. Shows error messages when subscription fails
 * 3. Provides feedback during subscription state changes
 *
 * Key features:
 * - Loading indicator
 * - Error message display
 * - Accessible switch control
 */

export const WebPushSwitch: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isSubscribed, toggleSubscription } = useWebPush();

  const handleToggle = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await toggleSubscription();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("webPushToggleError");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="flex flex-col justify-between gap-2 py-2">
      <div className="flex items-center justify-between">
        <span>{t("webPushToggleLabel")}</span>
        <div className="flex items-center gap-2">
          {isLoading && <Loader size={16} className="text-muted-foreground" />}
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={isLoading}
            aria-label={t("webPushToggleLabel")}
          />
        </div>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
